import { useMutation } from '@apollo/client';
import moment from 'moment';
import React from 'react';
import getVerificationStatus, {
  VerificationStatus,
} from './getVerificationStatus';
import {
  calculateExpirationTimestamp,
  humanReadableFrequency,
} from './verificationDateHelpers';
import VerifiedIcon from './VerifiedIcon';
import VerifiedToast from './VerifiedToast';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Form, { Field, Submit } from '../components/Form';
import Modal, { ModalActionFooter, ModalTitle } from '../components/Modal';
import Timestamp from '../components/Timestamp';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UIActions } from '../ui/uiSlice';
import yearSensitiveDateString from '../utils/yearSensitiveDateString';

const POST_FRAGMENT = gql(`
  fragment VerifyPostModal_post on Post {
    id
    verification {
      id
      unit
      quantity
    }
    verificationEntries {
      id
      verifiedAt
    }
    ...getVerificationStatus_post
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const VERIFY_POST_MUTATION = gql(/** @complexity 16 */ `
  mutation verifyPost($verificationId: ID!) {
    verificationEntry: verifyPost(verificationId: $verificationId) {
      id
      post {
        id
        verifiedAt
        verificationEntries {
          id
          verifiedAt
          expiredAt
          verifier {
            id
            name
            title
            ...UserAvatar
          }
        }
      }
    }
  }
`);

interface VerifyPostModalProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  close: () => void;
  noChanges?: boolean;
}

type FormValues = { confirm: boolean };

const VerifyPostModal = ({ post, close, noChanges }: VerifyPostModalProps) => {
  const dispatch = useAppDispatch();
  const newExpirationDateString = post.verification
    ? yearSensitiveDateString(
        calculateExpirationTimestamp(
          moment().valueOf(),
          post.verification,
        ).unsafe(),
      )
    : '';
  const frequencyString = post.verification
    ? humanReadableFrequency(post.verification)
    : '';

  let subtitle;
  const status = getVerificationStatus(post);
  if (status === VerificationStatus.Requested) {
    subtitle = `Verification request is pending. Confirm its accuracy to verify for ${frequencyString}, until ${newExpirationDateString}.`;
  } else if (status === VerificationStatus.Expired) {
    if (noChanges) {
      subtitle = `Verification has expired, though no changes have been made to the post. Confirm its accuracy to verify for ${frequencyString}, until ${newExpirationDateString}.`;
    } else {
      subtitle = `Verification has expired. Confirm its accuracy to verify for ${frequencyString}, until ${newExpirationDateString}.`;
    }
  } else if (post.verification) {
    const expirationTimestamp = calculateExpirationTimestamp(
      post.verificationEntries[0]?.verifiedAt,
      post.verification,
    ).unsafe();

    subtitle = (
      <>
        Verification expires{' '}
        {moment(expirationTimestamp).diff(moment(), 'days') >= 1 ? (
          <Timestamp date={expirationTimestamp} />
        ) : (
          'today'
        )}
        . Confirm its accuracy to keep its badge for {frequencyString}, until{' '}
        {newExpirationDateString}.
      </>
    );
  }

  const [verifyPost] = useMutation(VERIFY_POST_MUTATION);
  const onSubmit = async () => {
    const { verification } = post;
    if (!verification) {
      return null;
    }
    return verifyPost({
      variables: { verificationId: verification.id },
    }).then(() => {
      close();
      dispatch(
        UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
          <VerifiedToast
            onDismiss={toastProps.onDismiss}
            unit={verification.unit}
            quantity={verification.quantity}
          />
        )),
      );
    });
  };

  return (
    <Modal width={515}>
      <ModalTitle subtitle={subtitle} close={close}>
        {status === VerificationStatus.Requested ? 'Verify' : 'Reverify'} post{' '}
        <VerifiedIcon inline size="large" />
      </ModalTitle>
      <Form<FormValues>
        onSubmit={onSubmit}
        initialValues={{
          confirm: false,
        }}
      >
        {({ isValid, dirty }) => (
          <>
            <Field
              name="confirm"
              label="I’ve reviewed this post for accuracy"
              type="checkbox"
              required
            />
            <ModalActionFooter
              primary={
                <Submit size="large" disabled={!isValid || !dirty}>
                  Verify
                </Submit>
              }
            />
          </>
        )}
      </Form>
    </Modal>
  );
};

export default VerifyPostModal;
